import { useCallback, useEffect, useState } from 'react';

type UseAsyncState<T> = {
  isLoading: boolean;
  error: Error & {
    errorKey: string;
    errorMessage: string;
  };
  value: T | null;
};

type useAsyncOptions<T> = {
  manual?: boolean;
  onError?: (error: UseAsyncState<T>['error']) => void;
  onSuccess?: (response: T) => void;
};

type UseAsyncContext<T> = UseAsyncState<T> & {
  trigger: () => Promise<void>;
};

export const useAsync = <T,>(
  callback: () => Promise<T>,
  options: useAsyncOptions<T> = {},
): UseAsyncContext<T> => {
  const { manual = false, onError, onSuccess } = options;
  const [state, setState] = useState<UseAsyncState<T>>({
    isLoading: false,
    error: null,
    value: null,
  });

  const trigger = useCallback(async () => {
    setState({
      isLoading: true,
      error: null,
      value: null,
    });
    try {
      const response = await callback();

      setState({
        isLoading: false,
        error: null,
        value: response,
      });

      onSuccess?.(response);
    } catch (error) {
      setState({
        isLoading: false,
        error: error,
        value: null,
      });

      onError?.(error);
    }
  }, [callback, onError, onSuccess]);

  useEffect(() => {
    if (manual === false) {
      trigger();
    }
  }, [manual, trigger]);

  return {
    ...state,
    trigger,
  };
};
